// src/hooks/useAccessibleOrgs.ts
//
// Sidebar-facing org list + "should we show a switcher?" gate.
//
// Returns the orgs a user can switch their workspace into, plus a
// `shouldShow` flag that's `true` only when:
//   • the user belongs to >1 org, OR
//   • the user is a real platform operator (admin / dev / trainer)
//
// Regular single-org users get `shouldShow === false` and the sidebar popover
// should render `null` — no fetch is issued, no unnecessary noise.
//
// Note (deferred follow-up): `src/components/admin/useAdminPanelOrgOptions.ts`
// still performs its own near-identical load for the Admin panel's `<select>`.
// Consolidating both call sites onto a shared loader is intentionally scoped
// out of this slice (dev-sync 6dabb83f) to keep the diff focused. When that
// merge happens, the loader logic below should move to a pure async helper
// and both hooks should call into it.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { requireSupabase } from "@/lib/supabase";

export type AccessibleOrg = { id: string; name: string };

export type AccessibleOrgsState = {
  /** Sorted by name. Empty until the first load resolves (or when `shouldShow` is false). */
  orgs: AccessibleOrg[];
  loading: boolean;
  error: string | null;
  /**
   * `true` when the caller should render org-switching UI. Gated on
   * `memberOrganizationIds.length > 1` OR real platform-operator role. Use
   * this as the "render null" check — it's stable across loading states.
   */
  shouldShow: boolean;
};

export function useAccessibleOrgs(): AccessibleOrgsState {
  const auth = useAuth();
  const sb = useMemo(() => requireSupabase(), []);

  const memberOrgIds = auth.memberOrganizationIds ?? [];
  // Real role check — impersonation must not unlock cross-org browsing for
  // anyone who isn't already a platform operator on the real account.
  const isPlatformOperator = auth.realHasRole(
    "platform_admin",
    "platform_dev",
    "platform_trainer",
  );
  const shouldShow = memberOrgIds.length > 1 || isPlatformOperator;

  const [orgs, setOrgs] = useState<AccessibleOrg[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache the IDs as a stable string so member-array reference churn doesn't
  // refire the effect on every auth-context tick.
  const memberOrgIdsKey = memberOrgIds.join(",");

  useEffect(() => {
    if (!shouldShow) {
      setOrgs([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // Platform admins can see every org. The RPC re-validates server-side
        // (`is_platform_role_at_least`) before returning the full list, so a
        // user with the role token but no DB membership cannot exfiltrate.
        if (auth.realHasRole("platform_admin")) {
          const { data: dbPlatformAdmin, error: gateError } = await sb.rpc(
            "is_platform_role_at_least",
            { p_min_role: "platform_admin" },
          );
          if (!gateError && dbPlatformAdmin === true) {
            const { data, error: rpcError } = await sb.rpc("platform_list_orgs");
            if (!rpcError && Array.isArray(data)) {
              if (cancelled) return;
              const list = (data as AccessibleOrg[])
                .map((o) => ({ id: o.id, name: o.name }))
                .sort((a, b) => a.name.localeCompare(b.name));
              setOrgs(list);
              setLoading(false);
              return;
            }
          }
        }

        const ids = memberOrgIdsKey ? memberOrgIdsKey.split(",").filter(Boolean) : [];
        if (ids.length === 0) {
          if (cancelled) return;
          setOrgs([]);
          setLoading(false);
          return;
        }
        const { data, error: queryError } = await sb
          .from("organizations")
          .select("id, name")
          .in("id", ids)
          .order("name", { ascending: true });
        if (cancelled) return;
        if (queryError || !Array.isArray(data)) {
          setOrgs([]);
          setError(queryError?.message ?? "Failed to load organizations");
          setLoading(false);
          return;
        }
        setOrgs(data as AccessibleOrg[]);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        setLoading(false);
        console.warn("[useAccessibleOrgs] load failed", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldShow, sb, memberOrgIdsKey, auth]);

  return { orgs, loading, error, shouldShow };
}
